import { loadProjectProgress, saveProjectProgress } from '../utils/projectProgress';
import React, { useState, useEffect } from 'react';
import BootcampCTA from './BootcampCTA';
import GitHubExport from './GitHubExport';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CyberCharlie, {
  MiniCharlie,
  project1IntroMessages,
  project2IntroMessages,
  project3IntroMessages,
  project4IntroMessages,
  project1StepMessages,
  project2StepMessages,
  project3StepMessages,
  project4StepMessages,
  // DevSecAI Projects
  snowflakeSecurityIntroMessages,
  databricksSecurityIntroMessages,
  sagemakerSecurityIntroMessages,
  colabLlmSecurityIntroMessages,
  n8nAgentSecurityIntroMessages,
  aiAgentIncidentResponseIntroMessages,
  snowflakeSecurityStepMessages,
  databricksSecurityStepMessages,
  sagemakerSecurityStepMessages,
  colabLlmSecurityStepMessages,
  n8nAgentSecurityStepMessages,
  aiAgentIncidentResponseStepMessages,
} from './CyberCharlie';

// Get intro messages based on project id
const getIntroMessages = (projectId) => {
  switch (projectId) {
    case 'devsecops-github-aws':
      return project1IntroMessages;
    case 'devsecops-gitlab-azure':
      return project2IntroMessages;
    case 'threat-modelling':
      return project3IntroMessages;
    case 'kubernetes-security':
      return project4IntroMessages;
    // DevSecAI Projects
    case 'snowflake-security':
      return snowflakeSecurityIntroMessages;
    case 'databricks-security':
      return databricksSecurityIntroMessages;
    case 'sagemaker-ml-security':
      return sagemakerSecurityIntroMessages;
    case 'colab-llm-security':
      return colabLlmSecurityIntroMessages;
    case 'n8n-agent-security':
      return n8nAgentSecurityIntroMessages;
    case 'ai-agent-incident-response':
      return aiAgentIncidentResponseIntroMessages;
    default:
      return project1IntroMessages;
  }
};

// Get step messages based on project id
const getStepMessages = (projectId) => {
  switch (projectId) {
    case 'devsecops-github-aws':
      return project1StepMessages;
    case 'devsecops-gitlab-azure':
      return project2StepMessages;
    case 'threat-modelling':
      return project3StepMessages;
    case 'kubernetes-security':
      return project4StepMessages;
    // DevSecAI Projects
    case 'snowflake-security':
      return snowflakeSecurityStepMessages;
    case 'databricks-security':
      return databricksSecurityStepMessages;
    case 'sagemaker-ml-security':
      return sagemakerSecurityStepMessages;
    case 'colab-llm-security':
      return colabLlmSecurityStepMessages;
    case 'n8n-agent-security':
      return n8nAgentSecurityStepMessages;
    case 'ai-agent-incident-response':
      return aiAgentIncidentResponseStepMessages;
    default:
      return {};
  }
};

// Get color variant based on project
const getColorVariant = (projectId) => {
  switch (projectId) {
    case 'devsecops-github-aws':
      return 'green';
    case 'devsecops-gitlab-azure':
      return 'orange';
    case 'threat-modelling':
      return 'purple';
    case 'kubernetes-security':
      return 'cyan';
    // DevSecAI Projects
    case 'snowflake-security':
      return 'ice';
    case 'databricks-security':
      return 'amber';
    case 'sagemaker-ml-security':
      return 'rose';
    case 'colab-llm-security':
      return 'lime';
    case 'n8n-agent-security':
      return 'indigo';
    case 'ai-agent-incident-response':
      return 'red';
    default:
      return 'green';
  }
};

// Get gradient colors based on project
const getProjectColors = (projectId) => {
  switch (projectId) {
    case 'devsecops-github-aws':
      return {
        primary: '#22c55e',
        secondary: '#10b981',
        gradient: 'linear-gradient(135deg, #064e3b 0%, #1e293b 100%)',
        badge: '#dcfce7',
        badgeText: '#166534',
      };
    case 'devsecops-gitlab-azure':
      return {
        primary: '#f97316',
        secondary: '#ea580c',
        gradient: 'linear-gradient(135deg, #7c2d12 0%, #1e293b 100%)',
        badge: '#ffedd5',
        badgeText: '#9a3412',
      };
    case 'threat-modelling':
      return {
        primary: '#a855f7',
        secondary: '#9333ea',
        gradient: 'linear-gradient(135deg, #581c87 0%, #1e293b 100%)',
        badge: '#f3e8ff',
        badgeText: '#7c3aed',
      };
    case 'kubernetes-security':
      return {
        primary: '#06b6d4',
        secondary: '#0891b2',
        gradient: 'linear-gradient(135deg, #164e63 0%, #1e293b 100%)',
        badge: '#cffafe',
        badgeText: '#0e7490',
      };
    // DevSecAI Projects
    case 'snowflake-security':
      return {
        primary: '#0ea5e9',
        secondary: '#0284c7',
        gradient: 'linear-gradient(135deg, #0c4a6e 0%, #1e293b 100%)',
        badge: '#e0f2fe',
        badgeText: '#0369a1',
      };
    case 'databricks-security':
      return {
        primary: '#f59e0b',
        secondary: '#d97706',
        gradient: 'linear-gradient(135deg, #78350f 0%, #1e293b 100%)',
        badge: '#fef3c7',
        badgeText: '#b45309',
      };
    case 'sagemaker-ml-security':
      return {
        primary: '#f43f5e',
        secondary: '#e11d48',
        gradient: 'linear-gradient(135deg, #881337 0%, #1e293b 100%)',
        badge: '#ffe4e6',
        badgeText: '#be123c',
      };
    case 'colab-llm-security':
      return {
        primary: '#84cc16',
        secondary: '#65a30d',
        gradient: 'linear-gradient(135deg, #3f6212 0%, #1e293b 100%)',
        badge: '#ecfccb',
        badgeText: '#4d7c0f',
      };
    case 'n8n-agent-security':
      return {
        primary: '#6366f1',
        secondary: '#4f46e5',
        gradient: 'linear-gradient(135deg, #312e81 0%, #1e293b 100%)',
        badge: '#e0e7ff',
        badgeText: '#4338ca',
      };
    case 'ai-agent-incident-response':
      return {
        primary: '#ef4444',
        secondary: '#dc2626',
        gradient: 'linear-gradient(135deg, #7f1d1d 0%, #1e293b 100%)',
        badge: '#fee2e2',
        badgeText: '#b91c1c',
      };
    default:
      return {
        primary: '#6366f1',
        secondary: '#4f46e5',
        gradient: 'linear-gradient(135deg, #312e81 0%, #1e293b 100%)',
        badge: '#e0e7ff',
        badgeText: '#4338ca',
      };
  }
};

// Company context data for each project (like Cyber Agoge portal)
const getCompanyContext = (projectId) => {
  switch (projectId) {
    case 'devsecops-github-aws':
      return {
        company: 'NexusCore Technologies',
        type: 'Series A Fintech',
        stats: [
          { label: 'Daily Transactions', value: '$2M' },
          { label: 'Engineering Team', value: '45 developers' },
          { label: 'Security Incidents', value: '3 this quarter' },
        ],
        mission: 'Implement a complete DevSecOps pipeline to secure the CI/CD process before the next funding round.',
      };
    case 'devsecops-gitlab-azure':
      return {
        company: 'DataFlow Analytics (NexusCore Acquisition)',
        type: 'Enterprise Data Platform',
        stats: [
          { label: 'Data Processed', value: '5TB/day' },
          { label: 'Engineering Team', value: '120 developers' },
          { label: 'Compliance Status', value: 'SOC 2 Required' },
        ],
        mission: 'Build an enterprise-grade DevSecOps pipeline using GitLab CI/CD and Azure to meet compliance requirements.',
      };
    case 'threat-modelling':
      return {
        company: 'SecureBank Financial',
        type: 'Digital Banking Platform',
        stats: [
          { label: 'Customer Accounts', value: '2.5M' },
          { label: 'Annual Revenue', value: '$150M' },
          { label: 'Regulatory Bodies', value: '4 jurisdictions' },
        ],
        mission: 'Conduct comprehensive threat modelling using STRIDE, MITRE ATT&CK, and DREAD frameworks.',
      };
    case 'kubernetes-security':
      return {
        company: 'CloudScale Systems',
        type: 'SaaS Infrastructure Provider',
        stats: [
          { label: 'Kubernetes Clusters', value: '15 production' },
          { label: 'Containers Running', value: '2,500+' },
          { label: 'Uptime SLA', value: '99.99%' },
        ],
        mission: 'Implement comprehensive Kubernetes security using Trivy, Falco, OPA, and network policies.',
      };
    // DevSecAI Consulting Projects
    case 'snowflake-security':
      return {
        company: 'DevSecAI Consulting',
        type: "World's Leading AI Security Consultancy",
        stats: [
          { label: 'Client', value: 'FinanceFlow Inc.' },
          { label: 'Data Volume', value: '50TB daily' },
          { label: 'Compliance', value: 'SOC 2, PCI-DSS' },
        ],
        mission: 'Secure a financial analytics data warehouse handling sensitive customer transactions and PII.',
      };
    case 'databricks-security':
      return {
        company: 'DevSecAI Consulting',
        type: "World's Leading AI Security Consultancy",
        stats: [
          { label: 'Client', value: 'HealthData Analytics' },
          { label: 'Data Lake Size', value: '200PB' },
          { label: 'Compliance', value: 'HIPAA, GDPR' },
        ],
        mission: 'Implement security controls for a healthcare lakehouse platform processing protected health information.',
      };
    case 'sagemaker-ml-security':
      return {
        company: 'DevSecAI Consulting',
        type: "World's Leading AI Security Consultancy",
        stats: [
          { label: 'Client', value: 'InsureTech AI' },
          { label: 'Models in Production', value: '45' },
          { label: 'Predictions/Day', value: '10M+' },
        ],
        mission: 'Secure machine learning pipelines for an insurance company using AI for fraud detection and risk assessment.',
      };
    case 'colab-llm-security':
      return {
        company: 'DevSecAI Consulting',
        type: "World's Leading AI Security Consultancy",
        stats: [
          { label: 'Client', value: 'LegalAI Solutions' },
          { label: 'LLM Applications', value: '12' },
          { label: 'User Base', value: '50K lawyers' },
        ],
        mission: 'Red team and secure LLM-powered legal research tools against prompt injection and data exfiltration attacks.',
      };
    case 'n8n-agent-security':
      return {
        company: 'DevSecAI Consulting',
        type: "World's Leading AI Security Consultancy",
        stats: [
          { label: 'Client', value: 'AutomateX Corp' },
          { label: 'Active Workflows', value: '500+' },
          { label: 'API Integrations', value: '150' },
        ],
        mission: 'Secure AI-powered automation workflows that handle sensitive customer data and critical business processes.',
      };
    case 'ai-agent-incident-response':
      return {
        company: 'DevSecAI Consulting',
        type: "World's Leading AI Security Consultancy",
        stats: [
          { label: 'Client', value: 'MegaCorp Financial' },
          { label: 'Affected Accounts', value: '2.3M' },
          { label: 'Status', value: 'CRITICAL BREACH' },
        ],
        mission: 'Lead incident response for a compromised AI agent that is exfiltrating customer data and making unauthorized transactions.',
      };
    default:
      return null;
  }
};

const ProjectViewer = ({ project, onBack }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedTasks, setCompletedTasks] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizResult, setShowQuizResult] = useState({});
  const [expandedInstructions, setExpandedInstructions] = useState({});
  const [expandedFiles, setExpandedFiles] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);

  // CyberCharlie state
  const [showCharlie, setShowCharlie] = useState(false);
  const [hasSeenIntro, setHasSeenIntro] = useState(false);
  const [showMiniCharlie, setShowMiniCharlie] = useState(false);
  const [currentMiniMessage, setCurrentMiniMessage] = useState('');
  const [seenStepMessages, setSeenStepMessages] = useState({});

  // GitHub Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  const [progressRecord, setProgressRecord] = useState(null);
  const [saveStatus, setSaveStatus] = useState('Loading saved progress…');
  const [saving, setSaving] = useState(false);
  const storageKey = progressRecord ? `tekyu-project-account:${progressRecord.id}` : null;
  const introStorageKey = `tekyu-project-${project.id}-intro-seen`;
  const stepMessagesKey = `tekyu-project-${project.id}-step-messages`;
  const colors = getProjectColors(project.id);
  const colorVariant = getColorVariant(project.id);
  const stepMessages = getStepMessages(project.id);
  const companyContext = getCompanyContext(project.id);

  // Check if intro has been seen
  useEffect(() => {
    const introSeen = localStorage.getItem(introStorageKey);
    if (introSeen) {
      setHasSeenIntro(true);
      setShowCharlie(false);
    } else {
      setShowCharlie(true);
    }
    // Load seen step messages
    const seenSteps = localStorage.getItem(stepMessagesKey);
    if (seenSteps) {
      try { setSeenStepMessages(JSON.parse(seenSteps)); } catch { setSeenStepMessages({}); }
    }
  }, [introStorageKey, stepMessagesKey]);

  useEffect(() => {
    let cancelled = false;
    setProgressRecord(null);
    setCompletedTasks({}); setCurrentStepIndex(0); setQuizAnswers({});
    loadProjectProgress(project.id).then(record => {
      if (cancelled) return;
      setProgressRecord(record);
      if (record.data) {
        setCompletedTasks(record.data.completedTasks || {});
        setCurrentStepIndex(Math.max(0, Math.min(record.data.currentStepIndex || 0, project.steps.length - 1)));
        setQuizAnswers(record.data.quizAnswers || {});
      }
      setSaveStatus(record.data ? 'Saved progress loaded from your account.' : 'Use Save progress to keep your work across devices.');
    }).catch(() => { if (!cancelled) setSaveStatus('Could not load account progress. Sign in and reload before saving.'); });
    return () => { cancelled = true; };
  }, [project.id, project.steps.length]);

  const restoreLegacyDraft = () => {
    try {
      const old = JSON.parse(localStorage.getItem(`tekyu-project-${project.id}`) || 'null');
      if (!old) { setSaveStatus('No older draft found on this browser.'); return; }
      setCompletedTasks(old.completedTasks || {});
      setCurrentStepIndex(Math.max(0, Math.min(old.currentStepIndex || 0, project.steps.length - 1)));
      setSaveStatus('Older browser draft restored. Check it is yours, then Save progress.');
    } catch { setSaveStatus('The older browser draft could not be read.'); }
  };
  const saveProgress = async () => {
    if (!progressRecord || saving) return;
    setSaving(true); setSaveStatus('Saving…');
    const data = { completedTasks, currentStepIndex, quizAnswers, lastUpdated: new Date().toISOString() };
    try {
      const record = await saveProjectProgress(progressRecord, data);
      setProgressRecord(record); setSaveStatus('Saved to your account.');
      try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch { /* Cloud save succeeded. */ }
    } catch (error) { setSaveStatus(error.message || 'Not saved. Please try again.'); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (progressRecord) setSaveStatus('Use Save progress before leaving to keep your latest changes.');
  }, [completedTasks, currentStepIndex, quizAnswers]);

  // Show Mini Charlie when navigating to a step with a message
  useEffect(() => {
    const stepId = currentStepIndex + 1; // steps are 1-indexed in messages
    const stepMessage = stepMessages[stepId];

    if (stepMessage && !seenStepMessages[stepId] && hasSeenIntro) {
      const timer = setTimeout(() => {
        setCurrentMiniMessage(stepMessage.text);
        setShowMiniCharlie(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [currentStepIndex, seenStepMessages, hasSeenIntro, stepMessages]);

  const handleCharlieComplete = () => {
    setShowCharlie(false);
    setHasSeenIntro(true);
    localStorage.setItem(introStorageKey, 'true');
  };

  const handleMiniCharlieDismiss = () => {
    const stepId = currentStepIndex + 1;
    const newSeenMessages = { ...seenStepMessages, [stepId]: true };
    setSeenStepMessages(newSeenMessages);
    localStorage.setItem(stepMessagesKey, JSON.stringify(newSeenMessages));
    setShowMiniCharlie(false);
  };

  const resetCharlie = () => {
    localStorage.removeItem(introStorageKey);
    localStorage.removeItem(stepMessagesKey);
    setHasSeenIntro(false);
    setSeenStepMessages({});
    setShowCharlie(true);
  };

  const currentStep = project.steps[currentStepIndex];

  const toggleTask = (stepId, taskId) => {
    setCompletedTasks((prev) => {
      const stepTasks = prev[stepId] || [];
      if (stepTasks.includes(taskId)) {
        return { ...prev, [stepId]: stepTasks.filter((id) => id !== taskId) };
      }
      return { ...prev, [stepId]: [...stepTasks, taskId] };
    });
  };

  const handleQuizAnswer = (stepId, questionIndex, answerIndex) => {
    setQuizAnswers((prev) => ({
      ...prev,
      [`${stepId}-${questionIndex}`]: answerIndex,
    }));
    setShowQuizResult((prev) => ({
      ...prev,
      [`${stepId}-${questionIndex}`]: true,
    }));
  };

  const getCompletedStepsCount = () => {
    return project.steps.filter((step) => {
      const stepTasks = completedTasks[step.id] || [];
      return step.content.tasks?.length === stepTasks.length;
    }).length;
  };

  const isStepComplete = (stepId) => {
    const step = project.steps.find((s) => s.id === stepId);
    const stepTasks = completedTasks[stepId] || [];
    return step?.content.tasks?.length === stepTasks.length;
  };

  const toggleInstruction = (id) => {
    setExpandedInstructions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleFileExpanded = (fileName) => {
    setExpandedFiles((prev) => ({ ...prev, [fileName]: !prev[fileName] }));
  };

  const copyToClipboard = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const completedTasksCount = Object.values(completedTasks).flat().length;
  const totalTasks = project.steps.reduce((acc, step) => acc + (step.content.tasks?.length || 0), 0);
  const progressPercentage = totalTasks > 0 ? Math.round((completedTasksCount / totalTasks) * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#111827' }}>
      {/* CyberCharlie Full Intro */}
      {showCharlie && (
        <CyberCharlie messages={getIntroMessages(project.id)} onComplete={handleCharlieComplete} position="right" />
      )}

      {/* Mini Charlie - Step-specific */}
      {showMiniCharlie && (
        <MiniCharlie message={currentMiniMessage} onDismiss={handleMiniCharlieDismiss} variant={colorVariant} />
      )}

      {/* Header */}
      <div
        style={{
          background: colors.gradient,
          borderBottom: '1px solid #374151',
          padding: '16px 24px',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <button
              onClick={onBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#9ca3af',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '0',
              }}
            >
              <span style={{ fontSize: '18px' }}>&larr;</span> Back to Projects
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* GitHub Export Button */}
              <button
                onClick={() => setShowExportModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: 'rgba(99, 102, 241, 0.2)',
                  color: '#a5b4fc',
                  border: '1px solid rgba(99, 102, 241, 0.4)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                <span>📦</span> Export to GitHub
              </button>
              {hasSeenIntro && (
                <button
                  onClick={resetCharlie}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#d1d5db',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  <span>🤖</span> Meet Cyber Charlie
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                boxShadow: `0 4px 12px ${colors.primary}40`,
              }}
            >
              {project.icon || '📚'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span
                  style={{
                    backgroundColor: colors.badge,
                    color: colors.badgeText,
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                >
                  PRO
                </span>
                <span style={{ color: '#9ca3af', fontSize: '13px' }}>{project.difficulty}</span>
                <span style={{ color: '#6b7280' }}>•</span>
                <span style={{ color: '#9ca3af', fontSize: '13px' }}>{project.estimatedHours} hours</span>
              </div>
              <h1 style={{ color: 'white', fontSize: '22px', fontWeight: '700', margin: 0 }}>{project.title}</h1>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: colors.primary }}>{progressPercentage}%</div>
              <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                {completedTasksCount}/{totalTasks} tasks
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div
            style={{
              marginTop: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              height: '6px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progressPercentage}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                transition: 'width 0.5s ease',
              }}
            />
          </div>

          {/* Company Context Banner - Like Cyber Agoge */}
          {companyContext && (
            <div
              style={{
                marginTop: '20px',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                borderRadius: '12px',
                padding: '16px 20px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                {/* Company Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                    }}
                  >
                    🏢
                  </div>
                  <div>
                    <div style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>{companyContext.company}</div>
                    <div style={{ color: '#a5b4fc', fontSize: '13px' }}>{companyContext.type}</div>
                  </div>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  {companyContext.stats.map((stat, index) => (
                    <div key={index} style={{ textAlign: 'center' }}>
                      <div style={{ color: '#c4b5fd', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {stat.label}
                      </div>
                      <div style={{ color: 'white', fontSize: '16px', fontWeight: '700' }}>{stat.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mission */}
              <div
                style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(139, 92, 246, 0.2)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '14px' }}>🎯</span>
                  <p style={{ color: '#e0e7ff', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                    <strong style={{ color: '#c4b5fd' }}>Your Mission:</strong> {companyContext.mission}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main content with sidebar */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar - Step Navigation */}
        <div
          style={{
            width: '280px',
            backgroundColor: '#1f2937',
            borderRight: '1px solid #374151',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <button type="button" onClick={saveProgress} disabled={!progressRecord || saving} className="rounded-lg bg-green-500 px-4 py-2 text-slate-950 disabled:opacity-50">{saving ? 'Saving…' : 'Save progress'}</button>
            <span role="status" className="text-sm text-slate-300">{saveStatus}</span>
            <button type="button" onClick={restoreLegacyDraft} disabled={!progressRecord} className="text-sm underline text-slate-300">Restore my older browser draft</button>
            <p className="w-full text-xs text-slate-400">Save before leaving. Older drafts are not linked to an account; restore only your own work.</p>
          </div>
          {/* Steps progress */}
          <div style={{ padding: '16px', borderBottom: '1px solid #374151' }}>
            <div style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '4px' }}>
              Step {currentStepIndex + 1} of {project.steps.length}
            </div>
            <div style={{ color: '#d1d5db', fontSize: '14px' }}>
              {getCompletedStepsCount()} steps complete
            </div>
          </div>

          {/* Steps list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {project.steps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => setCurrentStepIndex(index)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: currentStepIndex === index ? `${colors.primary}20` : 'transparent',
                  border: currentStepIndex === index ? `1px solid ${colors.primary}50` : '1px solid transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <span
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: isStepComplete(step.id) ? '#10b981' : currentStepIndex === index ? colors.primary : '#374151',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    flexShrink: 0,
                  }}
                >
                  {isStepComplete(step.id) ? '✓' : step.icon || index + 1}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      color: currentStepIndex === index ? 'white' : '#d1d5db',
                      fontSize: '14px',
                      fontWeight: currentStepIndex === index ? '600' : '400',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {step.title}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '2px',
                    }}
                  >
                    <span
                      style={{
                        color: '#9ca3af',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {step.description}
                    </span>
                    {step.content.tasks?.length > 0 && (
                      <span
                        style={{
                          backgroundColor: isStepComplete(step.id) ? '#10b981' : '#374151',
                          color: isStepComplete(step.id) ? 'white' : '#9ca3af',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {(completedTasks[step.id] || []).length}/{step.content.tasks.length} tasks
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main content area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '32px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Step header */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    boxShadow: `0 4px 12px ${colors.primary}40`,
                  }}
                >
                  {currentStep.icon}
                </div>
                <div>
                  <div style={{ color: colors.primary, fontSize: '14px', fontWeight: '500' }}>
                    Step {currentStepIndex + 1} of {project.steps.length}
                  </div>
                  <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', margin: 0 }}>{currentStep.title}</h1>
                </div>
              </div>
              <p style={{ color: '#9ca3af', fontSize: '16px', marginLeft: '72px' }}>{currentStep.description}</p>
            </div>

            {/* Overview */}
            {currentStep.content.overview && (
              <div
                style={{
                  backgroundColor: '#1f2937',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px',
                  border: '1px solid #374151',
                }}
              >
                <h3 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📋</span> Overview
                </h3>
                <p style={{ color: '#d1d5db', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{currentStep.content.overview}</p>
              </div>
            )}

            {/* Instructions - Expandable */}
            {currentStep.content.instructions?.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📝</span> Instructions
                </h3>
                {currentStep.content.instructions.map((instruction, index) => (
                  <div
                    key={instruction.id || index}
                    style={{
                      backgroundColor: '#1f2937',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      border: '1px solid #374151',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      onClick={() => toggleInstruction(instruction.id || index)}
                      style={{
                        padding: '16px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: `${colors.primary}20`,
                          color: colors.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '700',
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </div>
                      <h4 style={{ color: 'white', fontSize: '16px', fontWeight: '500', flex: 1, margin: 0 }}>{instruction.title}</h4>
                      <svg
                        style={{
                          width: '20px',
                          height: '20px',
                          color: '#9ca3af',
                          transition: 'transform 0.2s',
                          transform: expandedInstructions[instruction.id || index] ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {expandedInstructions[instruction.id || index] && (
                      <div style={{ padding: '0 20px 20px', borderTop: '1px solid #374151' }}>
                        <div style={{ color: '#d1d5db', lineHeight: '1.7', whiteSpace: 'pre-wrap', marginTop: '16px' }}>{instruction.details}</div>

                        {/* Warning box */}
                        {instruction.warning && (
                          <div
                            style={{
                              marginTop: '16px',
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              borderRadius: '8px',
                              padding: '12px 16px',
                              borderLeft: `4px solid #ef4444`,
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <span style={{ color: '#ef4444' }}>⚠️</span>
                              <p style={{ color: '#fca5a5', margin: 0 }}>{instruction.warning}</p>
                            </div>
                          </div>
                        )}

                        {/* Tip box */}
                        {instruction.tip && (
                          <div
                            style={{
                              marginTop: '16px',
                              backgroundColor: `${colors.primary}10`,
                              borderRadius: '8px',
                              padding: '12px 16px',
                              borderLeft: `4px solid ${colors.primary}`,
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <span style={{ color: colors.primary }}>💡</span>
                              <p style={{ color: colors.primary, margin: 0 }}>{instruction.tip}</p>
                            </div>
                          </div>
                        )}

                        {/* Code block */}
                        {instruction.codeBlock && (
                          <div style={{ marginTop: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <span style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase' }}>
                                {instruction.codeBlock.language || 'code'}
                              </span>
                              <button
                                onClick={() => copyToClipboard(instruction.codeBlock.code, instruction.id)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#374151',
                                  color: '#d1d5db',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                {copiedCode === instruction.id ? '✓ Copied!' : 'Copy'}
                              </button>
                            </div>
                            <SyntaxHighlighter
                              language={instruction.codeBlock.language || 'bash'}
                              style={oneDark}
                              customStyle={{
                                borderRadius: '8px',
                                padding: '16px',
                                fontSize: '14px',
                                margin: 0,
                              }}
                              showLineNumbers={instruction.codeBlock.code?.split('\n').length > 5}
                            >
                              {instruction.codeBlock.code}
                            </SyntaxHighlighter>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Files Reference */}
            {currentStep.content.files?.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📄</span> Files Reference
                </h3>
                {currentStep.content.files.map((file) => (
                  <div
                    key={file.name}
                    style={{
                      backgroundColor: '#1f2937',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      border: '1px solid #374151',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      onClick={() => toggleFileExpanded(file.name)}
                      style={{
                        padding: '16px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '20px' }}>📄</span>
                        <div>
                          <div style={{ color: 'white', fontSize: '15px', fontWeight: '500' }}>{file.name}</div>
                          <div style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'monospace' }}>{file.path}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(file.code, file.name);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#374151',
                            color: '#d1d5db',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          {copiedCode === file.name ? '✓ Copied!' : 'Copy All'}
                        </button>
                        <svg
                          style={{
                            width: '20px',
                            height: '20px',
                            color: '#9ca3af',
                            transition: 'transform 0.2s',
                            transform: expandedFiles[file.name] ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {expandedFiles[file.name] && (
                      <div style={{ borderTop: '1px solid #374151' }}>
                        {/* File explanation */}
                        {file.explanation && (
                          <div
                            style={{
                              padding: '16px 20px',
                              backgroundColor: `${colors.primary}10`,
                              borderBottom: '1px solid #374151',
                            }}
                          >
                            <p style={{ color: colors.primary, margin: 0, fontSize: '14px' }}>{file.explanation}</p>
                          </div>
                        )}

                        {/* Vulnerability Notes */}
                        {file.vulnerabilityNotes?.length > 0 && (
                          <div
                            style={{
                              padding: '16px 20px',
                              backgroundColor: 'rgba(0, 0, 0, 0.2)',
                              borderBottom: '1px solid #374151',
                            }}
                          >
                            <h4 style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Security Notes
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {file.vulnerabilityNotes.map((note, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '10px',
                                    fontSize: '13px',
                                    color: note.type === 'security' ? '#f87171' : note.type === 'warning' ? '#fbbf24' : '#60a5fa',
                                  }}
                                >
                                  <span
                                    style={{
                                      backgroundColor: '#374151',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontFamily: 'monospace',
                                      fontSize: '11px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    L{note.line}
                                  </span>
                                  <span>{note.message}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Code */}
                        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                          <SyntaxHighlighter
                            language={file.language}
                            style={oneDark}
                            showLineNumbers
                            customStyle={{
                              margin: 0,
                              borderRadius: 0,
                              background: '#1e293b',
                              fontSize: '13px',
                            }}
                          >
                            {file.code}
                          </SyntaxHighlighter>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Tasks */}
            {currentStep.content.tasks?.length > 0 && (
              <div
                style={{
                  backgroundColor: '#1f2937',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px',
                  border: '1px solid #374151',
                }}
              >
                <h3 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✅</span> Tasks to Complete
                </h3>
                {currentStep.content.tasks.map((task) => {
                  const isCompleted = (completedTasks[currentStep.id] || []).includes(task.id);
                  return (
                    <label
                      key={task.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '14px 16px',
                        backgroundColor: isCompleted ? '#064e3b' : '#374151',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isCompleted}
                        onChange={() => toggleTask(currentStep.id, task.id)}
                        style={{
                          width: '20px',
                          height: '20px',
                          marginTop: '2px',
                          accentColor: '#10b981',
                          cursor: 'pointer',
                        }}
                      />
                      <span
                        style={{
                          color: isCompleted ? '#a7f3d0' : '#d1d5db',
                          textDecoration: isCompleted ? 'line-through' : 'none',
                        }}
                      >
                        {task.text}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Quiz */}
            {currentStep.content.quiz?.length > 0 && (
              <div
                style={{
                  backgroundColor: '#1f2937',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px',
                  border: '1px solid #374151',
                }}
              >
                <h3 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🧠</span> Knowledge Check
                </h3>
                {currentStep.content.quiz.map((question, qIndex) => {
                  const answerKey = `${currentStep.id}-${qIndex}`;
                  const selectedAnswer = quizAnswers[answerKey];
                  const showResult = showQuizResult[answerKey];
                  const isCorrect = selectedAnswer === question.correctAnswer;

                  return (
                    <div key={qIndex} style={{ marginBottom: qIndex < currentStep.content.quiz.length - 1 ? '24px' : 0 }}>
                      <p style={{ color: 'white', fontWeight: '500', marginBottom: '12px' }}>{question.question}</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {question.options.map((option, oIndex) => {
                          const isSelected = selectedAnswer === oIndex;
                          const isCorrectAnswer = oIndex === question.correctAnswer;

                          let bgColor = '#374151';
                          let borderColor = '#4b5563';

                          if (showResult) {
                            if (isCorrectAnswer) {
                              bgColor = '#064e3b';
                              borderColor = '#10b981';
                            } else if (isSelected && !isCorrectAnswer) {
                              bgColor = '#7f1d1d';
                              borderColor = '#ef4444';
                            }
                          } else if (isSelected) {
                            bgColor = `${colors.primary}20`;
                            borderColor = colors.primary;
                          }

                          return (
                            <button
                              key={oIndex}
                              onClick={() => !showResult && handleQuizAnswer(currentStep.id, qIndex, oIndex)}
                              disabled={showResult}
                              style={{
                                padding: '12px 16px',
                                backgroundColor: bgColor,
                                border: `2px solid ${borderColor}`,
                                borderRadius: '8px',
                                color: '#d1d5db',
                                textAlign: 'left',
                                cursor: showResult ? 'default' : 'pointer',
                                transition: 'all 0.2s',
                              }}
                            >
                              {option}
                            </button>
                          );
                        })}
                      </div>
                      {showResult && (
                        <div
                          style={{
                            marginTop: '12px',
                            padding: '12px 16px',
                            backgroundColor: isCorrect ? '#064e3b' : '#1e3a5f',
                            borderRadius: '8px',
                          }}
                        >
                          <p style={{ color: isCorrect ? '#a7f3d0' : '#93c5fd', fontWeight: '600', marginBottom: '4px' }}>
                            {isCorrect ? '✓ Correct!' : '✗ Not quite.'}
                          </p>
                          <p style={{ color: isCorrect ? '#a7f3d0' : '#93c5fd', margin: 0 }}>{question.explanation}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Navigation buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '24px',
                borderTop: '1px solid #374151',
              }}
            >
              <button
                onClick={() => setCurrentStepIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentStepIndex === 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: currentStepIndex === 0 ? '#374151' : '#4b5563',
                  color: currentStepIndex === 0 ? '#6b7280' : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: currentStepIndex === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                &larr; Previous
              </button>
              <button
                onClick={() => setCurrentStepIndex((prev) => Math.min(project.steps.length - 1, prev + 1))}
                disabled={currentStepIndex === project.steps.length - 1}
                style={{
                  padding: '12px 24px',
                  backgroundColor: currentStepIndex === project.steps.length - 1 ? '#374151' : colors.primary,
                  color: currentStepIndex === project.steps.length - 1 ? '#6b7280' : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: currentStepIndex === project.steps.length - 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                Next &rarr;
              </button>
            </div>

            {/* Bootcamp CTA - show every 3 steps or at the end */}
            {(currentStepIndex > 0 && currentStepIndex % 3 === 0) || currentStepIndex === project.steps.length - 1 ? (
              <div style={{ marginTop: '24px' }}>
                <BootcampCTA variant="inline" context="project" />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* GitHub Export Modal */}
      {showExportModal && (
        <GitHubExport
          project={project}
          completedTasks={completedTasks}
          colors={colors}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

export default ProjectViewer;
